import React, { useEffect, useCallback, useState } from 'react';
import { adminApi, OnboardingApplication, PaginatedApplicationsResponse } from '../../services/adminApi';
import { useAdminStore } from '../../store/adminStore';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { ApplicationFilters } from './components/ApplicationFilters';
import { ApplicationsTable } from './components/ApplicationsTable';
import { ApplicationDetailModal } from './components/ApplicationDetailModal';
import { AdminOnboardCustomer } from './components/AdminOnboardCustomer';
import styles from './AdminOnboardingDashboard.module.css';

const EMPTY_DATA: PaginatedApplicationsResponse = {
  data: [],
  pagination: { total: 0, page: 1, limit: 20, totalPages: 0 } as any,
};

export const AdminOnboardingDashboard: React.FC = () => {
  const {
    searchQuery,
    statusFilter,
    sortBy,
    sortOrder,
    currentPage,
    itemsPerPage,
    selectedApplicationId,
    detailModalOpen,
    successMessage,
    error: adminError,
    setLoading,
    setError,
    setSuccessMessage,
    closeDetailModal,
    closeApproveDialog,
    closeRejectDialog,
  } = useAdminStore();

  const [data, setData] = useState<PaginatedApplicationsResponse>(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [onboardingMode, setOnboardingMode] = useState(false);

  // Fetch applications when filters / pagination change
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoading(true);
    adminApi
      .getOnboardingApplications({
        page: currentPage,
        limit: itemsPerPage,
        status: statusFilter === 'all' ? undefined : (statusFilter as any),
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
      })
      .then((res) => {
        if (cancelled) return;
        setData(res ?? EMPTY_DATA);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load applications');
        setData(EMPTY_DATA);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [searchQuery, statusFilter, sortBy, sortOrder, currentPage, itemsPerPage, reloadTick, setLoading, setError]);

  const selectedApplication = data?.data.find((app) => app.id === selectedApplicationId) || null;

  const handleApply = useCallback(async () => {
    if (!selectedApplicationId) return;
    setLoading(true);
    try {
      const result = await adminApi.approveApplication(selectedApplicationId);
      setSuccessMessage(`Application approved! Account created for ${result.user?.email ?? 'applicant'}`);
      closeApproveDialog();
      closeDetailModal();
      setReloadTick((t) => t + 1);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve application');
    } finally {
      setLoading(false);
    }
  }, [selectedApplicationId, setLoading, setSuccessMessage, setError, closeApproveDialog, closeDetailModal]);

  const handleReject = useCallback(
    async (reason: string, notes: string) => {
      if (!selectedApplicationId) return;
      setLoading(true);
      try {
        const result = await adminApi.rejectApplication(selectedApplicationId, reason, notes);
        setSuccessMessage(`Application rejected. Notification sent to ${result.application?.email ?? 'applicant'}`);
        closeRejectDialog();
        closeDetailModal();
        setReloadTick((t) => t + 1);
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to reject application');
      } finally {
        setLoading(false);
      }
    },
    [selectedApplicationId, setLoading, setSuccessMessage, setError, closeRejectDialog, closeDetailModal]
  );

  const handleRowClick = useCallback(
    (application: OnboardingApplication) => {
      const { openDetailModal } = useAdminStore.getState();
      openDetailModal(application.id);
    },
    []
  );

  const handleSearch = useCallback(() => {
    setReloadTick((t) => t + 1);
  }, []);

  // Admin-initiated onboarding takes over the page with the reused customer form.
  if (onboardingMode) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Onboard New Customer</h1>
            <p className={styles.subtitle}>Complete the application on the customer's behalf — it's approved and the account is created immediately.</p>
          </div>
        </div>
        <AdminOnboardCustomer
          onCancel={() => setOnboardingMode(false)}
          onDone={(email) => {
            setOnboardingMode(false);
            setReloadTick((t) => t + 1);
            setSuccessMessage(`Customer onboarded! Account created for ${email}`);
            setTimeout(() => setSuccessMessage(null), 4000);
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Customer Onboarding Applications</h1>
          <p className={styles.subtitle}>Review and manage customer onboarding submissions</p>
        </div>
        <button className={styles.onboardBtn} onClick={() => setOnboardingMode(true)}>
          + Onboard Customer
        </button>
      </div>

      {successMessage && (
        <div style={{ margin: '0 0 16px', padding: 12, background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0', borderRadius: 8, fontSize: 14 }}>
          {successMessage}
        </div>
      )}
      {adminError && (
        <div style={{ margin: '0 0 16px', padding: 12, background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: 8, fontSize: 14 }}>
          {adminError}
        </div>
      )}

      <ApplicationFilters onSearch={handleSearch} />

      <ApplicationsTable
        applications={data?.data || []}
        isLoading={isLoading}
        totalItems={data?.pagination?.total || 0}
        onRowClick={handleRowClick}
      />

      {detailModalOpen && selectedApplication && (
        <ApplicationDetailModal
          application={selectedApplication}
          onClose={closeDetailModal}
          onApprove={handleApply}
          onReject={handleReject}
        />
      )}
    </div>
  );
};

export const AdminOnboardingDashboardPage: React.FC = () => {
  return (
    <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
      <AdminOnboardingDashboard />
    </ProtectedRoute>
  );
};
